import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, ShieldCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { Guide } from "@/components/Bits";
import { GradeBox } from "@/components/GradeBox";
import { useAuth } from "@/context/AuthContext";

const field =
  "w-full px-4 py-3 rounded-xl border border-slate-300 bg-white text-sm focus:outline-none focus:border-blue-500 transition";

const GOALS = [
  { key: "catch_up", label: "Catch up", hint: "I've fallen behind and want to close the gap" },
  { key: "keep_up", label: "Keep up with school", hint: "Stay steady with what I'm studying now" },
  { key: "go_deeper", label: "Go deeper than school", hint: "School isn't enough — I want more" },
  { key: "board_exam", label: "Prepare for an exam", hint: "There's a test I need to be ready for" },
  { key: "career_explore", label: "Explore a career", hint: "I want to see where this could take me" },
  { key: "build", label: "Build something", hint: "I have something I want to make" },
  { key: "curiosity", label: "Just curious", hint: "I want to understand how things work" },
];

const SITUATIONS = [
  { key: "in_school", label: "I'm in school" },
  { key: "homeschooled", label: "I learn at home" },
  { key: "never", label: "I've never been to school" },
  { key: "break", label: "I took a break from school" },
  { key: "finished", label: "I've finished school" },
  { key: "unsure", label: "I'd rather not say" },
];

const COMFORT = [
  ["confident", "Confident"],
  ["okay", "Okay"],
  ["shaky", "Shaky"],
  ["never", "Never studied it"],
];

const AREAS = [
  ["maths", "Maths"],
  ["science", "Science"],
  ["computing", "Computing"],
];

const BARRIERS = [
  "Nothing, I'm good",
  "My attention wanders",
  "Reading is slow for me",
  "Maths makes me anxious",
  "Exams make me nervous",
  "Dyslexia or ADHD",
];

const STYLES = ["Worked examples", "Pictures and diagrams", "Stories", "Hands-on", "Small steps", "Talking it through"];
const INTERESTS = ["Space", "Games", "Football", "Music", "Animals", "Cars", "Robots", "Drawing", "Cooking", "History", "Money", "Nature"];

const Chip = ({ active, onClick, children, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors active:scale-95 ${
      active ? "bg-slate-900 border-slate-900 text-white" : "border-slate-300 text-slate-700 hover:border-slate-500"
    }`}
  >
    {children}
  </button>
);

const toggle = (list, value) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [p, setP] = useState({
    name: user?.name || "",
    dob: "",
    grade: "",
    gradeTouched: false,
    country: "",
    language: "English",
    situation: "",
    last_grade_completed: "",
    last_year: "",
    comfort: {},
    barriers: [],
    learning_style: [],
    interests: [],
    days_per_week: 5,
    daily_topic_minutes: 30,
    stuck_pref: "questions",
    guardian_email: "",
    goal_type: "",
    goal_detail: "",
    goal_why: "",
    exam_name: "",
    exam_date: "",
  });

  const set = (patch) => setP({ ...p, ...patch });
  const age = (() => {
    if (!p.dob) return 0;
    const b = new Date(p.dob);
    const t = new Date();
    let a = t.getFullYear() - b.getFullYear();
    if (t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate())) a -= 1;
    return a > 0 && a < 100 ? a : 0;
  })();
  const suggestedGrade = age ? String(Math.min(12, Math.max(1, age - 5))) : "";
  const grade = p.gradeTouched ? p.grade : suggestedGrade;
  const topicsEstimate = Math.round(p.days_per_week * 4.3 * 3 * Math.min(2, p.daily_topic_minutes / 30));
  const canNext = { 1: p.name.trim() && p.dob && age > 0, 2: true, 3: !!p.goal_type }[step];

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/profile", {
        name: p.name.trim(),
        dob: p.dob,
        grade,
        country: p.country,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
        language: p.language,
        schooling: {
          situation: p.situation,
          last_grade_completed: p.last_grade_completed,
          last_year: p.last_year,
          comfort: p.comfort,
          barriers: p.barriers,
          learning_style: p.learning_style,
        },
        interests: p.interests,
        days_per_week: Number(p.days_per_week),
        daily_topic_minutes: Number(p.daily_topic_minutes),
        stuck_pref: p.stuck_pref,
        guardian_email: p.guardian_email.trim(),
        goal: {
          type: p.goal_type,
          detail: p.goal_detail,
          why: p.goal_why,
          exam_name: p.exam_name,
          exam_date: p.exam_date,
          subjects: [],
          duration_months: 3,
        },
      });
      await refresh();
      if (data.guardian_invite_link) toast.success(`Guardian invite link: ${data.guardian_invite_link}`, { duration: 12000 });
      toast.success("Profile saved — let's build your curriculum");
      navigate("/curriculum");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const guideLine = {
    1: "Just your name and date of birth to start. Your age tells us how to pitch the language — you'll choose the level yourself in a moment.",
    2: "There's no wrong answer here. Wherever you're starting from is where we start, and none of these questions are required.",
    3: "This is the part that shapes everything. Say what you want in your own words — the more honest, the better the plan.",
  }[step];

  return (
    <Shell nav={[]}>
      <div className="max-w-3xl mx-auto reveal" data-testid="onboarding-root">
        <p className="rule-label">Getting set up · step {step} of 3</p>
        <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold">
          {step === 1 ? "About you" : step === 2 ? "Where you're at" : "What you want"}
        </h1>

        <div className="mt-5 flex gap-2" data-testid="onboarding-progress">
          {[1, 2, 3].map((s) => (
            <div key={s} className={`h-1 flex-1 rounded-full ${s <= step ? "bg-blue-600" : "bg-slate-200"}`} />
          ))}
        </div>

        <div className="mt-6">
          <Guide testId="onboarding-guide">{guideLine}</Guide>
        </div>

        {step === 1 && (
          <div className="mt-6 card-flat p-7 space-y-5" data-testid="onboarding-step-1">
            <div>
              <label className="rule-label">What should we call you?</label>
              <input data-testid="ob-name" className={`${field} mt-2`} value={p.name} onChange={(e) => set({ name: e.target.value })} placeholder="Your first name is enough" />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="rule-label">Date of birth</label>
                <input data-testid="ob-dob" type="date" className={`${field} mt-2`} value={p.dob} onChange={(e) => set({ dob: e.target.value })} />
                {age > 0 && (
                  <p className="mt-2 text-sm text-slate-500" data-testid="ob-age">
                    {age} years old.
                  </p>
                )}
              </div>
              <div>
                <label className="rule-label">Country (optional)</label>
                <input data-testid="ob-country" className={`${field} mt-2`} value={p.country} onChange={(e) => set({ country: e.target.value })} placeholder="India" />
              </div>
            </div>

            {age > 0 && (
              <div data-testid="ob-grade-block">
                <GradeBox
                  testId="ob-grade-box"
                  grade={p.gradeTouched ? p.grade : suggestedGrade}
                  recommended={suggestedGrade}
                  age={age}
                  onChange={(g) => set({ grade: g, gradeTouched: true })}
                />
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="mt-6 card-flat p-7 space-y-6" data-testid="onboarding-step-2">
            <div>
              <label className="rule-label">Are you in school right now?</label>
              <div className="mt-3 flex flex-wrap gap-2">
                {SITUATIONS.map((s) => (
                  <Chip key={s.key} testId={`ob-situation-${s.key}`} active={p.situation === s.key} onClick={() => set({ situation: s.key })}>
                    {s.label}
                  </Chip>
                ))}
              </div>
            </div>

            {["in_school", "homeschooled", "break", "finished"].includes(p.situation) && (
              <div className="grid sm:grid-cols-2 gap-5" data-testid="ob-history-block">
                <div>
                  <label className="rule-label">Last grade you finished</label>
                  <select data-testid="ob-last-grade" className={`${field} mt-2`} value={p.last_grade_completed} onChange={(e) => set({ last_grade_completed: e.target.value })}>
                    <option value="">I don't remember</option>
                    {[...Array(12)].map((_, i) => (
                      <option key={i} value={String(i + 1)}>
                        Grade {i + 1}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="rule-label">Roughly when? (optional)</label>
                  <input data-testid="ob-last-year" className={`${field} mt-2`} value={p.last_year} onChange={(e) => set({ last_year: e.target.value })} placeholder="2025" />
                </div>
              </div>
            )}

            <div>
              <label className="rule-label">How do these feel right now?</label>
              <div className="mt-3 space-y-3">
                {AREAS.map(([key, label]) => (
                  <div key={key} className="flex flex-wrap items-center gap-2">
                    <span className="w-24 text-sm font-medium text-slate-700">{label}</span>
                    {COMFORT.map(([value, text]) => (
                      <Chip
                        key={value}
                        testId={`ob-comfort-${key}-${value}`}
                        active={p.comfort[key] === value}
                        onClick={() => set({ comfort: { ...p.comfort, [key]: value } })}
                      >
                        {text}
                      </Chip>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="rule-label">Anything that makes learning harder? (private, optional)</label>
              <div className="mt-3 flex flex-wrap gap-2">
                {BARRIERS.map((b) => (
                  <Chip key={b} testId={`ob-barrier-${b.slice(0, 6)}`} active={p.barriers.includes(b)} onClick={() => set({ barriers: toggle(p.barriers, b) })}>
                    {b}
                  </Chip>
                ))}
              </div>
            </div>

            <div>
              <label className="rule-label">You learn best with…</label>
              <div className="mt-3 flex flex-wrap gap-2">
                {STYLES.map((s) => (
                  <Chip key={s} testId={`ob-style-${s.slice(0, 6)}`} active={p.learning_style.includes(s)} onClick={() => set({ learning_style: toggle(p.learning_style, s) })}>
                    {s}
                  </Chip>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="mt-6 card-flat p-7 space-y-6" data-testid="onboarding-step-3">
            <div>
              <label className="rule-label">What brings you here?</label>
              <div className="mt-3 grid sm:grid-cols-2 gap-3">
                {GOALS.filter((g) => !(g.key === "board_exam" && age && age < 9)).map((g) => (
                  <button
                    key={g.key}
                    type="button"
                    data-testid={`ob-goal-${g.key}`}
                    onClick={() => set({ goal_type: g.key })}
                    className={`text-left p-4 rounded-2xl border transition-colors ${
                      p.goal_type === g.key ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-400"
                    }`}
                  >
                    <p className="font-semibold">{g.label}</p>
                    <p className="mt-1 text-sm text-slate-500">{g.hint}</p>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="rule-label">In your own words</label>
              <textarea
                data-testid="ob-goal-detail"
                rows={4}
                className={`${field} mt-2`}
                value={p.goal_detail}
                onChange={(e) => set({ goal_detail: e.target.value })}
                placeholder="What do you want to be able to do a few months from now? Say it however you like — this is what your plan is built from."
              />
            </div>

            {p.goal_type === "board_exam" && (
              <div className="grid sm:grid-cols-2 gap-5" data-testid="ob-exam-block">
                <div>
                  <label className="rule-label">Which exam?</label>
                  <input data-testid="ob-exam-name" className={`${field} mt-2`} value={p.exam_name} onChange={(e) => set({ exam_name: e.target.value })} />
                </div>
                <div>
                  <label className="rule-label">When is it?</label>
                  <input data-testid="ob-exam-date" type="date" className={`${field} mt-2`} value={p.exam_date} onChange={(e) => set({ exam_date: e.target.value })} />
                </div>
              </div>
            )}

            <div>
              <label className="rule-label">What are you into? (used for real-world examples)</label>
              <div className="mt-3 flex flex-wrap gap-2">
                {INTERESTS.map((i) => (
                  <Chip key={i} testId={`ob-interest-${i}`} active={p.interests.includes(i)} onClick={() => set({ interests: toggle(p.interests, i) })}>
                    {i}
                  </Chip>
                ))}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="rule-label">Days a week</label>
                <select data-testid="ob-days" className={`${field} mt-2`} value={p.days_per_week} onChange={(e) => set({ days_per_week: e.target.value })}>
                  {[2, 3, 4, 5, 6, 7].map((d) => (
                    <option key={d} value={d}>
                      {d} days
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="rule-label">Minutes a day</label>
                <select data-testid="ob-minutes" className={`${field} mt-2`} value={p.daily_topic_minutes} onChange={(e) => set({ daily_topic_minutes: e.target.value })}>
                  {[15, 20, 30, 45, 60, 90].map((m) => (
                    <option key={m} value={m}>
                      {m} minutes
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <p className="text-sm text-slate-500" data-testid="ob-pace">
              That's about one topic a day — roughly {topicsEstimate} topics over three months.
            </p>

            <div>
              <label className="rule-label">When you're stuck, what helps?</label>
              <div className="mt-3 flex flex-wrap gap-2">
                <Chip testId="ob-stuck-questions" active={p.stuck_pref === "questions"} onClick={() => set({ stuck_pref: "questions" })}>
                  Ask me questions until I get it
                </Chip>
                <Chip testId="ob-stuck-explain" active={p.stuck_pref === "explain"} onClick={() => set({ stuck_pref: "explain" })}>
                  Just explain it clearly
                </Chip>
              </div>
            </div>

            <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-blue-600" />
                <p className="rule-label">Guardian — entirely optional</p>
              </div>
              <p className="mt-2 text-sm text-slate-500">
                Add one and they can follow your progress and help when the teacher can't land something. Skip it and
                nothing changes — nobody has to approve your plan.
              </p>
              <input
                data-testid="ob-guardian-email"
                type="email"
                className={`${field} mt-3`}
                value={p.guardian_email}
                onChange={(e) => set({ guardian_email: e.target.value })}
                placeholder="guardian@email.com (leave empty to skip)"
              />
            </div>
          </div>
        )}

        <div className="mt-7 flex flex-wrap items-center gap-3">
          {step > 1 && (
            <button data-testid="ob-back" onClick={() => setStep(step - 1)} className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-slate-300 text-sm font-semibold hover:border-slate-500 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          )}
          {step < 3 ? (
            <button
              data-testid="ob-next"
              disabled={!canNext}
              onClick={() => setStep(step + 1)}
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-40 transition-colors"
            >
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              data-testid="ob-submit"
              disabled={!canNext || saving}
              onClick={submit}
              className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-40 transition-colors"
            >
              {saving ? "Saving…" : "Save and continue"} <Check className="w-4 h-4" />
            </button>
          )}
          {step === 2 && (
            <button data-testid="ob-skip" onClick={() => setStep(3)} className="text-sm font-semibold text-slate-500 hover:text-slate-900 transition-colors">
              Skip this step
            </button>
          )}
          <span className="ml-auto text-xs text-slate-400">You can change any of this later.</span>
        </div>
      </div>
    </Shell>
  );
}
